import {NumberOutput} from "@cm/template-nodes"
import {createNumberInput, createNumberExport, createTemplateInput, createMathOperator} from "#mattress/assets-definitions/common"
import {
    createMattressCurveInput,
    createMattressComponentInstance,
    createMattressComponentGraph,
    createMattressComponnentGraphGenerator,
} from "#mattress/assets-definitions/create-mattress"
import {MATTRESS_BASE_MESH} from "#mattress/validation"
import {createPath, seamPathName, uiPathMetaData} from "#mattress/mattress-meta-data"

export function getComponentGraphNodesForAccessoryStackElement() {
    // ref curve
    const refCurveInput = createMattressCurveInput("Ref Curve", "ref-curve")

    // inner accessory instance
    const accessoryGraphInput = createTemplateInput("Accessory", "accessory")
    const accessoryInstance = createMattressComponentInstance("Accessory", "accessory", accessoryGraphInput)

    // stack size
    const accessorySize = accessoryInstance.createOutput("size", {source: "template", outputNodeClass: NumberOutput, name: "Accessory Size"})
    if (!(accessorySize instanceof NumberOutput)) throw new Error("Accessory size output must be a NumberOutput") // TODO

    const priorStackSizeInput = createNumberInput("Prior Stack Size", "prior-stack-size", 0)
    const stackSize = createMathOperator("Stack Size", "add", priorStackSizeInput, accessorySize)
    const stackSizeExport = createNumberExport("Stack Size", "stack-size", stackSize)

    // stack element offset
    const accessoryStartOffsetInput = createNumberInput("Accessory Start Offset", "accessory-start-offset", 0)
    const accessoryEndOffset = createMathOperator("Accessory End Offset", "add", accessoryStartOffsetInput, accessorySize)
    const accessoryEndOffsetExport = createNumberExport("Accessory End Offset", "accessory-end-offset", accessoryEndOffset)
    const accessoryHalfSize = createMathOperator("Accessory Half Size", "mul", accessorySize, 0.5)
    const accessoryCenterOffset = createMathOperator("Accessory Center Offset", "add", accessoryStartOffsetInput, accessoryHalfSize)

    accessoryInstance.updateParameters({
        parameters: {
            "ref-curve": refCurveInput,
            "base-mesh": MATTRESS_BASE_MESH,
            offset: accessoryCenterOffset,
        },
    })

    return {
        nodes: [
            refCurveInput,
            accessoryGraphInput,
            accessoryInstance,
            accessorySize,
            priorStackSizeInput,
            stackSize,
            stackSizeExport,
            accessoryStartOffsetInput,
            accessoryEndOffset,
            accessoryEndOffsetExport,
            accessoryHalfSize,
            accessoryCenterOffset,
        ],
        exports: {
            stackSize: stackSizeExport,
            accessoryEndOffset: accessoryEndOffsetExport,
        },
    }
}

export function getComponentGraphForAccessoryStack(rank: number) {
    const refCurveInput = createMattressCurveInput("Ref Curve", "ref-curve")
    const accessoryCount = createNumberInput("Accessory Count", "accessory-count", 0, uiPathMetaData([createPath(seamPathName, rank)], 0))

    const {nodes: accessoryStackElementNodes, exports: accessoryStackElementExports} = getComponentGraphNodesForAccessoryStackElement()
    const accessoryStackElement = createMattressComponentGraph("Accessory Stack Element", accessoryStackElementNodes)

    const accessoryStackBaseRefCurveInput = createMattressCurveInput("Ref Curve", "ref-curve")
    const accessoryStackBase = createMattressComponentGraph("Accessory Stack Base", [accessoryStackBaseRefCurveInput])

    const accessoryStackGraph = createMattressComponnentGraphGenerator("Accessory Stack", {
        type: "MattressComponentGraphGeneratorUnitRepeat1D",
        unitGraph: accessoryStackElement,
        baseGraph: accessoryStackBase,
        unitInputs: {
            "ref-curve": accessoryStackBaseRefCurveInput,
            "prior-stack-size": accessoryStackElementExports.stackSize,
            "accessory-start-offset": accessoryStackElementExports.accessoryEndOffset,
        },
        unitIdPrefix: "accessory",
        outputsIfUnitCountZero: {
            "stack-size": 0,
            "accessory-end-offset": 0,
        },
        unitCount: accessoryCount,
    })
    const accessoryStack = createMattressComponentInstance("Accessory Stack", "accessory-stack", accessoryStackGraph)
    const accessoryStackSize = accessoryStack.createOutput("stack-size", {source: "template", outputNodeClass: NumberOutput, name: "Accessory Stack Size"})
    if (!(accessoryStackSize instanceof NumberOutput)) throw new Error("Accessory stack size output must be a NumberOutput") // TODO

    const stackStartOffset = createMathOperator("Stack Start Offset", "mul", accessoryStackSize, -0.5)
    accessoryStack.updateParameters({
        parameters: {
            "ref-curve": refCurveInput,
            "accessory-0/accessory-start-offset": stackStartOffset,
            "accessory-0/prior-stack-size": 0,
        },
    })

    return {
        nodes: [
            refCurveInput,
            accessoryCount,
            accessoryStackElement,
            accessoryStackBase,
            accessoryStackGraph,
            accessoryStack,
            accessoryStackSize,
            stackStartOffset,
        ],
    }
}
